/**
 * 云图渲染 + 像素读取 + 鼠标交互 composable
 */
import { ref } from 'vue';
import L from 'leaflet';
import { CLOUD_IMG_BOUNDS } from '@/config/map.config';

// 修补 Leaflet ImageOverlay 原型：防止图层被移除后残留事件监听导致空指针
// _animateZoom 由 zoomanim 事件触发，访问 this._map.getZoomScale()
const _origAnimateZoom = L.ImageOverlay.prototype._animateZoom;
L.ImageOverlay.prototype._animateZoom = function (e) {
  if (!this._map) return;
  _origAnimateZoom.call(this, e);
};
// _reset 由 zoom / viewreset 事件触发，访问 this._map.latLngToLayerPoint()
const _origReset = L.ImageOverlay.prototype._reset;
L.ImageOverlay.prototype._reset = function () {
  if (!this._map) return;
  _origReset.call(this);
};

export function useCloudOverlay(mapRef) {
  const overlayLayers = ref([]);

  /**
   * 安全移除图层
   */
  const safeRemoveLayer = (layer) => {
    if (mapRef.value && mapRef.value.hasLayer(layer)) {
      mapRef.value.removeLayer(layer);
    }
  };

  // 云量点击信息弹窗
  const cloudInfo = ref({
    visible: false,
    lat: 0,
    lng: 0,
    percent: 0,
    x: 0,
    y: 0
  });

  // 鼠标实时悬浮提示
  const cursorTip = ref({
    visible: false,
    lat: 0,
    lng: 0,
    percent: 0,
    x: 0,
    y: 0
  });

  /**
   * 渲染云图到地图上
   * @param {string} imgUrl - 图片URL
   */
  const renderCloud = (imgUrl) => {
    if (!mapRef.value) return;

    // 添加新图层前，同步清理多余旧层（只保留最近1帧作为兜底显示，防止快速播放时堆积）
    while (overlayLayers.value.length > 1) {
      const stale = overlayLayers.value.shift();
      safeRemoveLayer(stale);
    }

    const obj = L.imageOverlay(
      imgUrl,
      CLOUD_IMG_BOUNDS,
      { opacity: 1, interactive: true, bubblingMouseEvents: false, className: 'cloud-overlay', pane: 'cloudPane', crossOrigin: 'anonymous' }
    ).addTo(mapRef.value);

    overlayLayers.value.push(obj);

    // 像素数据用于计算云量百分比（复用 Leaflet 内部已加载的 <img> 元素，避免二次请求）
    let pixelData = null;
    let imgNaturalWidth = 0;
    let imgNaturalHeight = 0;

    obj.on("load", function () {
      // 新图层加载完毕，移除它之前的所有旧图层（实现无闪烁过渡）
      const idx = overlayLayers.value.indexOf(obj);
      if (idx > 0) {
        const oldLayers = overlayLayers.value.splice(0, idx);
        oldLayers.forEach(l => safeRemoveLayer(l));
      }

      // 直接从 Leaflet overlay 内部的 <img> 元素读取像素数据，无需二次请求
      const imgEl = obj._image || obj.getElement();
      if (imgEl) {
        imgNaturalWidth = imgEl.naturalWidth;
        imgNaturalHeight = imgEl.naturalHeight;
        const canvas = document.createElement('canvas');
        canvas.width = imgNaturalWidth;
        canvas.height = imgNaturalHeight;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(imgEl, 0, 0);
        try {
          pixelData = ctx.getImageData(0, 0, imgNaturalWidth, imgNaturalHeight).data;
        } catch (err) {
          console.error('获取像素数据失败(可能是跨域 CORS 问题):', err);
        }
      }
    });

    // 工具函数：根据鼠标事件获取经纬度+云量百分比
    const getCloudInfoFromEvent = (e) => {
      if (!pixelData) return null;
      const x = e.containerPoint.x;
      const y = e.containerPoint.y;

      const nwLatLng = L.latLng(CLOUD_IMG_BOUNDS[1][0], CLOUD_IMG_BOUNDS[0][1]);
      const seLatLng = L.latLng(CLOUD_IMG_BOUNDS[0][0], CLOUD_IMG_BOUNDS[1][1]);
      const nwPoint = mapRef.value.latLngToContainerPoint(nwLatLng);
      const sePoint = mapRef.value.latLngToContainerPoint(seLatLng);

      const relX = (x - nwPoint.x) / (sePoint.x - nwPoint.x);
      const relY = (y - nwPoint.y) / (sePoint.y - nwPoint.y);
      if (relX < 0 || relX > 1 || relY < 0 || relY > 1) return null;

      const imgX = Math.floor(relX * imgNaturalWidth);
      const imgY = Math.floor(relY * imgNaturalHeight);
      const idx = (imgY * imgNaturalWidth + imgX) * 4;
      const a = pixelData[idx + 3];
      const percent = Math.round((a / 255) * 100);
      return { lat: e.latlng.lat, lng: e.latlng.lng, percent, viewX: e.originalEvent.clientX, viewY: e.originalEvent.clientY };
    };

    // 实时跟随鼠标的云量提示
    obj.on('mousemove', function (e) {
      const info = getCloudInfoFromEvent(e);
      if (!info) {
        cursorTip.value.visible = false;
        return;
      }
      cursorTip.value = {
        visible: true,
        lat: info.lat,
        lng: info.lng,
        percent: info.percent,
        x: info.viewX + 16,
        y: info.viewY + 12
      };
    });

    obj.on('mouseout', function () {
      cursorTip.value.visible = false;
    });
  };

  /**
   * 清除所有云图覆盖层
   */
  const clearOverlays = () => {
    overlayLayers.value.forEach((layer) => {
      safeRemoveLayer(layer);
    });
    overlayLayers.value = [];
  };

  return {
    cloudInfo,
    cursorTip,
    renderCloud,
    clearOverlays
  };
}
